import { useEffect, useState } from "react";
import DashboardLayout from "../components/DashboardLayout";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Vote, Plus, BarChart3, X, AlertTriangle, Send, Loader2, Sparkles, Wifi,
} from "lucide-react";
import { useRealtime } from "../hooks/useRealtime";

const POLL_TYPES = [
  { v: "yes_no", label: "Yes / No" },
  { v: "multiple_choice", label: "Multiple choice" },
  { v: "rating", label: "Rating (1-5)" },
];

const CRISIS_TYPES = [
  "room_change", "schedule_delay", "emergency_announcement",
  "security_update", "medical", "lost_item",
  "committee_crisis", "technical_disruption", "transport_update",
];

const AUDIENCES = [
  "all_delegates", "by_committee", "by_country",
  "parents", "school_coordinators", "chairs",
  "volunteers",
];

export default function PollsCrisisPage() {
  const { user } = useAuth();
  const [polls, setPolls] = useState([]);
  const [crises, setCrises] = useState([]);
  const [activePoll, setActivePoll] = useState(null); // results
  const [pollResult, setPollResult] = useState(null);

  const [openPollDialog, setOpenPollDialog] = useState(false);
  const [pollForm, setPollForm] = useState({
    event_id: "event-globex-2026",
    committee_id: "",
    question: "",
    poll_type: "yes_no",
    options: ["Option A", "Option B"],
    anonymous: false,
  });

  const [openCrisisDialog, setOpenCrisisDialog] = useState(false);
  const [crisisForm, setCrisisForm] = useState({
    event_id: "event-globex-2026",
    audience: "all_delegates",
    audience_filter: "",
    crisis_type: "emergency_announcement",
    title: "",
    message: "",
    priority: "urgent",
    channels: ["in_app", "whatsapp"],
  });

  const isDelegate = user?.role === "delegate";

  const load = async () => {
    api.get("/polls").then((r) => setPolls(r.data || []));
    if (!isDelegate) api.get("/crisis/history").then((r) => setCrises(r.data || []));
  };
  useEffect(() => { load(); }, []);  // eslint-disable-line

  // Live update: when the active-poll modal is open, listen to the WebSocket
  // and update results without polling.
  useRealtime(
    activePoll ? `poll:${activePoll.id}` : null,
    (msg) => {
      if (msg.action === "poll_update" && msg.results) {
        setPollResult(msg.results);
      }
    }
  );

  // Crisis push toasts for non-staff users
  useRealtime(
    isDelegate ? `notifications:${user?.id}` : null,
    (msg) => {
      if (msg.action === "notification" && msg.notification?.kind === "crisis") {
        toast.warning(msg.notification.title, { description: msg.notification.body });
      }
    }
  );

  const createPoll = async (e) => {
    e.preventDefault();
    try {
      await api.post("/polls", pollForm);
      toast.success("Poll opened");
      setOpenPollDialog(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't create poll");
    }
  };

  const closePoll = async (poll) => {
    try {
      await api.post(`/polls/${poll.id}/close`);
      toast.success("Poll closed");
      load();
    } catch (err) {
      toast.error("Couldn't close");
    }
  };

  const showResults = async (poll) => {
    setActivePoll(poll);
    const { data } = await api.get(`/polls/${poll.id}/results`);
    setPollResult(data);
  };

  const vote = async (poll, option) => {
    try {
      await api.post("/polls/vote", { poll_id: poll.id, option });
      toast.success(`Voted: ${option}`);
      const { data } = await api.get(`/polls/${poll.id}/results`);
      setPollResult(data);
      setActivePoll(poll);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't vote");
    }
  };

  const sendCrisis = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/crisis/push", crisisForm);
      toast.success(`Crisis push sent to ${data.recipient_count} recipients (mock)`);
      setOpenCrisisDialog(false);
      setCrisisForm({ ...crisisForm, title: "", message: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't push");
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto pb-16">
        {/* Header */}
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
              Live Engagement
            </p>
            <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tighter text-[#0A192F]">
              Polls & <span className="font-serif italic text-[#0F4C81]">crisis push</span>.
            </h1>
            <p className="mt-2 text-sm text-slate-600 max-w-2xl">
              Run live polls during sessions and push emergency or operational alerts
              instantly across in-app, email and WhatsApp channels.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {!isDelegate && (
              <>
                <Dialog open={openPollDialog} onOpenChange={setOpenPollDialog}>
                  <DialogTrigger asChild>
                    <Button className="bg-[#0F4C81] hover:bg-[#0A3860]" data-testid="new-poll-button">
                      <Plus className="h-4 w-4 mr-1" /> New poll
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg">
                    <DialogHeader><DialogTitle>Create a live poll</DialogTitle></DialogHeader>
                    <form onSubmit={createPoll} className="space-y-3" data-testid="poll-form">
                      <div>
                        <Label className="text-xs">Question</Label>
                        <Input required value={pollForm.question} onChange={(e) => setPollForm({ ...pollForm, question: e.target.value })} data-testid="poll-question" />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Type</Label>
                          <select
                            value={pollForm.poll_type}
                            onChange={(e) => setPollForm({ ...pollForm, poll_type: e.target.value })}
                            className="mt-1.5 w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white"
                            data-testid="poll-type"
                          >
                            {POLL_TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
                          </select>
                        </div>
                        <div>
                          <Label className="text-xs">Committee ID (optional)</Label>
                          <Input value={pollForm.committee_id} onChange={(e) => setPollForm({ ...pollForm, committee_id: e.target.value })} />
                        </div>
                      </div>
                      {pollForm.poll_type === "multiple_choice" && (
                        <div>
                          <Label className="text-xs">Options (one per line)</Label>
                          <Textarea
                            rows={4}
                            value={pollForm.options.join("\n")}
                            onChange={(e) => setPollForm({ ...pollForm, options: e.target.value.split("\n").filter(Boolean) })}
                          />
                        </div>
                      )}
                      <label className="flex items-center gap-2 text-xs text-slate-700">
                        <input type="checkbox" checked={pollForm.anonymous} onChange={(e) => setPollForm({ ...pollForm, anonymous: e.target.checked })} />
                        Anonymous voting
                      </label>
                      <DialogFooter>
                        <Button type="submit" className="bg-[#0F4C81] hover:bg-[#0A3860]" data-testid="poll-submit">Open poll</Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>

                <Dialog open={openCrisisDialog} onOpenChange={setOpenCrisisDialog}>
                  <DialogTrigger asChild>
                    <Button className="bg-red-600 hover:bg-red-700 text-white" data-testid="new-crisis-button">
                      <AlertTriangle className="h-4 w-4 mr-1" /> Crisis push
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg">
                    <DialogHeader><DialogTitle className="text-red-700">Push a crisis alert</DialogTitle></DialogHeader>
                    <form onSubmit={sendCrisis} className="space-y-3" data-testid="crisis-form">
                      <div>
                        <Label className="text-xs">Title</Label>
                        <Input required value={crisisForm.title} onChange={(e) => setCrisisForm({ ...crisisForm, title: e.target.value })} placeholder="Room change · UNSC" data-testid="crisis-title" />
                      </div>
                      <div>
                        <Label className="text-xs">Message</Label>
                        <Textarea required rows={4} value={crisisForm.message} onChange={(e) => setCrisisForm({ ...crisisForm, message: e.target.value })} data-testid="crisis-message" />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Type</Label>
                          <select
                            value={crisisForm.crisis_type}
                            onChange={(e) => setCrisisForm({ ...crisisForm, crisis_type: e.target.value })}
                            className="mt-1.5 w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white"
                            data-testid="crisis-type"
                          >
                            {CRISIS_TYPES.map((t) => <option key={t} value={t}>{t.replace("_", " ")}</option>)}
                          </select>
                        </div>
                        <div>
                          <Label className="text-xs">Audience</Label>
                          <select
                            value={crisisForm.audience}
                            onChange={(e) => setCrisisForm({ ...crisisForm, audience: e.target.value })}
                            className="mt-1.5 w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white"
                            data-testid="crisis-audience"
                          >
                            {AUDIENCES.map((a) => <option key={a} value={a}>{a.replace("_", " ")}</option>)}
                          </select>
                        </div>
                      </div>
                      <DialogFooter>
                        <Button type="submit" className="bg-red-600 hover:bg-red-700" data-testid="crisis-submit">
                          <Send className="h-3.5 w-3.5 mr-1.5" /> Push to all channels
                        </Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </>
            )}
          </div>
        </div>

        {/* Polls list */}
        <div className="mt-8" data-testid="polls-section">
          <div className="flex items-center gap-2">
            <Vote className="h-4 w-4 text-[#0F4C81]" />
            <h2 className="text-lg font-semibold tracking-tight text-[#0A192F]">Live polls</h2>
          </div>
          <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {polls.length === 0 ? (
              <div className="col-span-3 p-10 rounded-xl bg-white border-dashed border-2 border-slate-200 text-center">
                <Vote className="h-6 w-6 text-slate-400 mx-auto" />
                <p className="mt-3 text-sm text-slate-500">No polls yet.</p>
              </div>
            ) : polls.map((p) => (
              <div key={p.id} className="rounded-xl bg-white border border-slate-200 p-5" data-testid={`poll-card-${p.id}`}>
                <div className="flex items-start justify-between">
                  <span className={`text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full ${p.status === "open" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                    {p.status}
                  </span>
                  {p.anonymous && <span className="text-[10px] text-slate-500 uppercase tracking-[0.18em]">Anonymous</span>}
                </div>
                <h3 className="mt-3 text-base font-semibold text-[#0A192F]">{p.question}</h3>
                <p className="mt-1 text-xs text-slate-500">{POLL_TYPES.find((t) => t.v === p.poll_type)?.label}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {isDelegate && p.status === "open" ? (
                    p.options.map((o) => (
                      <button
                        key={o}
                        onClick={() => vote(p, o)}
                        className="px-3 py-1.5 rounded-full bg-[#0F4C81] text-white text-xs font-bold hover:bg-[#0A3860]"
                        data-testid={`poll-vote-${o}`}
                      >
                        {o}
                      </button>
                    ))
                  ) : (
                    p.options.map((o) => <span key={o} className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 text-xs">{o}</span>)
                  )}
                </div>
                <div className="mt-4 flex items-center gap-3">
                  <button onClick={() => showResults(p)} className="text-xs uppercase tracking-[0.18em] font-bold text-[#0F4C81] inline-flex items-center gap-1.5" data-testid={`poll-results-${p.id}`}>
                    <BarChart3 className="h-3.5 w-3.5" /> Results
                  </button>
                  {!isDelegate && p.status === "open" && (
                    <button onClick={() => closePoll(p)} className="text-xs uppercase tracking-[0.18em] font-bold text-red-600 inline-flex items-center gap-1.5" data-testid={`poll-close-${p.id}`}>
                      <X className="h-3.5 w-3.5" /> Close
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Results modal */}
        {activePoll && pollResult && (
          <Dialog open onOpenChange={() => { setActivePoll(null); setPollResult(null); }}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {activePoll.question}
                  <span className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] font-bold px-2 py-1 rounded-full bg-emerald-100 text-emerald-700" data-testid="poll-live-pill">
                    <Wifi className="h-3 w-3" /> Live
                  </span>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm" data-testid="poll-result-view">
                {Object.entries(pollResult.counts).map(([opt, count]) => {
                  const pct = pollResult.percentages[opt];
                  return (
                    <div key={opt}>
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-semibold text-[#0A192F]">{opt}</span>
                        <span className="font-mono">{count} · {pct}%</span>
                      </div>
                      <div className="mt-1.5 h-2 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full rounded-full bg-gradient-to-r from-[#D4AF37] to-[#f6d272]" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
                <p className="text-[10px] text-slate-500 mt-3">{pollResult.total} votes total</p>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Crisis history */}
        {!isDelegate && (
          <div className="mt-10 rounded-2xl bg-white border border-slate-200 p-6" data-testid="crisis-history">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <h2 className="text-lg font-semibold tracking-tight text-[#0A192F]">Crisis push history</h2>
            </div>
            {crises.length === 0 ? (
              <p className="mt-4 text-sm text-slate-500">No crisis pushes sent yet.</p>
            ) : (
              <div className="mt-5 space-y-3">
                {crises.map((c) => (
                  <div key={c.id} className="rounded-md border border-red-100 bg-red-50/50 p-4">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <p className="font-semibold text-[#0A192F] text-sm">🚨 {c.title}</p>
                      <span className="text-[10px] text-slate-500 uppercase tracking-[0.18em] font-bold">
                        {c.crisis_type.replace("_", " ")} · {c.recipient_count} recipients
                      </span>
                    </div>
                    <p className="mt-2 text-sm text-slate-700">{c.message}</p>
                    <p className="mt-1 text-[10px] text-slate-500">
                      By {c.sent_by_name} · {new Date(c.created_at).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
